import { getPdfProxyUrl } from "../api/api";

const LOCAL_PREFIX = "local://";

/** Stable backend path for a PDF picked from the user's computer. The same file
 *  (name + size + last-modified) always maps to the same backend PDF record. */
export const localPdfKey = (file) =>
    `${LOCAL_PREFIX}${file.name}|${file.size}|${file.lastModified}`;

/** Local uploads have no URL the app can re-fetch later (e.g. on reload). */
export const isLocalPdfKey = (path) => (path || "").startsWith(LOCAL_PREFIX);

export const isPdfFile = (file) =>
    file && (file.type === "application/pdf" || /\.pdf$/i.test(file.name));

export const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/** Best-effort file name from a PDF URL — path segment first, then common query params. */
export const fileNameFromUrl = (url) => {
    try {
        const parsed = new URL(url);
        const last = parsed.pathname.split("/").pop();
        if (last && last.toLowerCase().endsWith(".pdf")) return decodeURIComponent(last);
        const param = parsed.searchParams.get("serve")
            || parsed.searchParams.get("file")
            || parsed.searchParams.get("filename");
        if (param && param.toLowerCase().endsWith(".pdf")) return decodeURIComponent(param);
    } catch {
        // not a parseable URL — fall through to the default
    }
    return "document.pdf";
};

/** Fetches a remote PDF through the backend proxy (avoids CORS) and returns a blob URL. */
export const fetchPdfBlobUrl = async (url, { timeoutMs = 60000 } = {}) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const response = await fetch(getPdfProxyUrl(url), {
            method: "GET",
            cache: "no-store",
            signal: controller.signal,
        });
        if (!response.ok) throw new Error(`Server error: ${response.status}`);
        const blob = await response.blob();
        if (!blob || blob.size === 0) throw new Error("Received an empty PDF file");
        return URL.createObjectURL(blob);
    } catch (err) {
        if (err.name === "AbortError") throw new Error("Request timed out. Please try again.");
        throw err;
    } finally {
        clearTimeout(timeoutId);
    }
};
